import type { SetStoreFunction } from "solid-js/store";
import { createStore } from "solid-js/store";
import type { ContextProviderComponent } from "solid-js";
import { createContext } from "solid-js";
import type { IssuePathItem } from "valibot";
import type { FieldType } from "../types";
import { initializedContext } from "../utils/initializedUtil";
import type FieldId from "../fields/FieldId";
import type RuleId from "../fields/RuleId";
import type SubformId from "./SubformId";
import type { FormScope } from "./formVersion";
import type IntegrationId from "../integrations/IntegrationId";
import type VariableId from "../variables/VariableId";
import type { VariableType } from "../variables/types";

export type IssueScope = "parent" | "field";

export type Issue = {
  path?: [IssuePathItem, ...IssuePathItem[]];
  message: string;
  scope?: IssueScope;
};

export type FieldError = {
  fieldId: FieldId;
  fieldType: FieldType;
  scope: FormScope;
  issues: Issue[];
};

export type VariableError = {
  variableId: VariableId;
  variableType: VariableType;
  issues: Issue[];
};

export type SubformError = {
  subformId: SubformId;
  fieldIds: FieldId[];
  ruleIds: RuleId[];
};

export type RuleError = {
  ruleId: RuleId;
  scope: FormScope;
  label: string;
  issues: Issue[];
};

export type IntegrationError = {
  integrationId: IntegrationId;
  issues: Issue[];
};

export type Validation = {
  fieldErrors: FieldError[];
  variableErrors: VariableError[];
  subformErrors: SubformError[];
  ruleErrors: RuleError[];
  integrationErrors: IntegrationError[];
};

type State = Validation;

type Dispatch = SetStoreFunction<Validation>;

const StateContext = createContext<State>();
const DispatchContext = createContext<Dispatch>();

export const ValidationProvider: ContextProviderComponent<undefined> = (props) => {
  const [validationStore, setValidationStore] = createStore<Validation>({
    fieldErrors: [],
    variableErrors: [],
    subformErrors: [],
    ruleErrors: [],
    integrationErrors: [],
  });

  return (
    <StateContext.Provider value={validationStore}>
      <DispatchContext.Provider value={setValidationStore}>{props.children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useValidationState = () => initializedContext(StateContext, "useValidationState", "FormProvider");
export const useValidationDispatch = () =>
  initializedContext(DispatchContext, "useValidationDispatch", "ValidationProvider");
